import { Button, useToast, Box, Flex } from '@chakra-ui/react';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'debounce-promise';
import { useAuth } from '../../contexts/auth';
import BoxContent from '../../components/BoxContent';
import FormDatePicker from '../../components/form/FormDatePicker';
import FormGroup from '../../components/form/FormGroup';
import TituloPagina from '../../components/TituloPagina';
import api from '../../services/api';
import AsyncSelect from '../../components/form/AsyncSelect';

type IUnidade = { uni_codigo: number; uni_sigla: string };

type OptionType = { label: string; value: string };

export const quantitativoSchema = yup.object().shape({
  dateInicio: yup.date().required('Este campo é requerido'),
  dateFim: yup.date().required('Este campo é requerido'),
  idUnidade: yup.number().notRequired(),
});

type IForm = {
  dateInicio: Date;
  dateFim: Date;
  idUnidade: number;
};

export const RelatorioQuantitativoPmsPorDia: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();
  const { handleSubmit, control, errors } = useForm<IForm>({
    resolver: yupResolver(quantitativoSchema),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState<OptionType>();

  const delayedQuery = debounce(async (query: string) => {
    const { data: unidades } = await api.get<IUnidade[]>('unidades', {
      params: { query, opm_usuario: user.opm?.uni_codigo },
    });

    return unidades?.map<OptionType>((unidade) => ({
      label: unidade.uni_sigla,
      value: unidade.uni_codigo.toString(),
    }));
  }, 500);

  const handleRequestRelatorio = async ({
    dateInicio,
    dateFim,
    idUnidade,
  }: IForm): Promise<void> => {
    setIsLoading(true);
    try {
      const { data: documento } = await api.get(
        '/escalas/irsos/relatorios/quantitativo-pms-por-dia',
        {
          params: {
            dataInicio: dateInicio,
            dataFim: dateFim,
            id_opm: idUnidade === -1 ? undefined : idUnidade,
          },
          responseType: 'blob',
        },
      );

      if (documento) {
        const url = window.URL.createObjectURL(new Blob([documento]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `quantitativo-pms-${format(dateInicio, 'dd-MM-yyyy')}-${format(
            dateFim,
            'dd-MM-yyyy',
          )}.pdf`,
        );

        document.body.appendChild(link);

        link.click();

        link.parentNode?.removeChild(link);
      }
    } catch (error) {
      if (error.response?.data) {
        const response = JSON.parse(await (error.response.data as Blob).text());
        toast({
          title: 'Ocorreu um erro.',
          description: response.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <TituloPagina title="Relatório Quantitativo de PMs por Dia" />
      <BoxContent>
        <TituloPagina title="Selecione o período para obter o relatório" />
        <Box as="form" onSubmit={handleSubmit(handleRequestRelatorio)}>
          <Flex>
            <FormGroup name="Data Início" required cols={[3, 3, 12]}>
              <Controller
                control={control}
                name="dateInicio"
                render={({ onChange, value, ...rest }) => (
                  <FormDatePicker
                    selected={value}
                    onChange={(e) => onChange(e as Date)}
                    dateFormat="dd/MM/yyyy"
                    error={errors.dateInicio?.message}
                    {...rest}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Data Fim" required cols={[3, 3, 12]}>
              <Controller
                control={control}
                name="dateFim"
                render={({ onChange, value, ...rest }) => (
                  <FormDatePicker
                    selected={value}
                    onChange={(e) => onChange(e as Date)}
                    dateFormat="dd/MM/yyyy"
                    error={errors.dateFim?.message}
                    {...rest}
                  />
                )}
              />
            </FormGroup>

            <FormGroup name="Pesquisar Opm" cols={[3, 6, 12]}>
              <Controller
                control={control}
                name="idUnidade"
                render={({ onChange, name }) => (
                  <AsyncSelect
                    name={name}
                    label="Pesquisar Opm"
                    value={unidadeSelecionada}
                    loadOptions={(value2: string) => delayedQuery(value2)}
                    onChange={(option: OptionType) => {
                      onChange(Number.parseInt(option.value, 10));
                      setUnidadeSelecionada(option);
                    }}
                    error={errors.idUnidade?.message}
                  />
                )}
              />
            </FormGroup>
          </Flex>
          <Button
            leftIcon={<HiOutlineDocumentReport />}
            colorScheme="blue"
            type="submit"
            isLoading={isLoading}
            loadingText="Gerando Relatorio..."
            ml="4"
            mt={7}
          >
            Gerar Relatório
          </Button>
        </Box>
      </BoxContent>
    </>
  );
};
